import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Building2, User, Mail, Phone, Globe, Briefcase, Send, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export default function WholesalePage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    website: '',
    business_type: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.business_name || !formData.contact_name || !formData.email || !formData.phone || !formData.business_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/wholesale/apply`, formData);
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="pt-20 min-h-screen flex flex-col items-center justify-center" data-testid="wholesale-success">
        <CheckCircle className="w-20 h-20 text-green-500 mb-6" />
        <h2 className="heading-display text-3xl text-white mb-4 text-center">Application Received!</h2>
        <p className="text-white/60 text-center max-w-md mb-8">
          Thank you for your interest in becoming a wholesale partner. Our team will review your application and get back to you within 2-3 business days.
        </p>
        <Link to="/">
          <Button className="btn-primary">Back to Home</Button>
        </Link>
      </div>
    );
  }

  const businessTypes = [
    'Retail Store',
    'Online Retailer',
    'Sports Collectibles Shop',
    'Card Shop',
    'Auction House',
    'Other'
  ];

  return (
    <div className="pt-20 min-h-screen" data-testid="wholesale-page">
      {/* Header */}
      <section className="py-16 border-b border-[#27272A] hero-glow">
        <div className="container-main">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors duration-200 mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <span className="label-caps text-[#C41E3A] mb-4 block">Partner With Us</span>
          <h1 className="heading-display text-4xl md:text-5xl text-white mb-4" data-testid="wholesale-title">
            Wholesale Application
          </h1>
          <p className="text-white/60 text-lg max-w-2xl">
            Join our wholesale program and get access to exclusive pricing, priority inventory, and dedicated support for your business.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 border-b border-[#27272A] bg-[#0A0A0A]">
        <div className="container-main">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="font-barlow font-bold text-white mb-2">Exclusive Pricing</h3>
              <p className="text-white/60 text-sm">Get wholesale rates on all authenticated memorabilia.</p>
            </div>
            <div>
              <h3 className="font-barlow font-bold text-white mb-2">Priority Access</h3>
              <p className="text-white/60 text-sm">First access to new arrivals and limited releases.</p>
            </div>
            <div>
              <h3 className="font-barlow font-bold text-white mb-2">Dedicated Support</h3>
              <p className="text-white/60 text-sm">Personal account manager for your business needs.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="section-gap">
        <div className="container-main max-w-2xl">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="wholesale-form">
            {/* Business Name */}
            <div>
              <label className="label-caps text-white/50 mb-2 block">Business Name *</label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  name="business_name"
                  placeholder="Your business name"
                  value={formData.business_name}
                  onChange={handleChange}
                  className="input-field pl-10"
                  data-testid="wholesale-business-name"
                />
              </div>
            </div>

            {/* Contact Name */}
            <div>
              <label className="label-caps text-white/50 mb-2 block">Contact Name *</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  name="contact_name"
                  placeholder="Your full name"
                  value={formData.contact_name}
                  onChange={handleChange}
                  className="input-field pl-10"
                  data-testid="wholesale-contact-name"
                />
              </div>
            </div>

            {/* Email & Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="label-caps text-white/50 mb-2 block">Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <Input
                    name="email"
                    type="email"
                    placeholder="you@business.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="input-field pl-10"
                    data-testid="wholesale-email"
                  />
                </div>
              </div>
              <div>
                <label className="label-caps text-white/50 mb-2 block">Phone *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <Input
                    name="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={handleChange}
                    className="input-field pl-10"
                    data-testid="wholesale-phone"
                  />
                </div>
              </div>
            </div>

            {/* Website */}
            <div>
              <label className="label-caps text-white/50 mb-2 block">Website (Optional)</label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  name="website"
                  type="url"
                  placeholder="https://yourbusiness.com"
                  value={formData.website}
                  onChange={handleChange}
                  className="input-field pl-10"
                  data-testid="wholesale-website"
                />
              </div>
            </div>

            {/* Business Type */}
            <div>
              <label className="label-caps text-white/50 mb-2 block">Business Type *</label>
              <Select value={formData.business_type} onValueChange={(value) => setFormData(prev => ({ ...prev, business_type: value }))}>
                <SelectTrigger className="input-field" data-testid="wholesale-business-type">
                  <Briefcase className="w-5 h-5 text-white/30 mr-2" />
                  <SelectValue placeholder="Select business type" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-[#27272A]">
                  {businessTypes.map((type) => (
                    <SelectItem key={type} value={type} className="text-white hover:bg-white/10">
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Message */}
            <div>
              <label className="label-caps text-white/50 mb-2 block">Additional Information (Optional)</label>
              <Textarea
                name="message"
                placeholder="Tell us about your business and what products you're interested in..."
                value={formData.message}
                onChange={handleChange}
                className="input-field min-h-[120px] resize-none"
                data-testid="wholesale-message"
              />
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="wholesale-submit"
            >
              {loading ? 'Submitting...' : 'Submit Application'}
              <Send className="w-5 h-5" />
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
